// @school-apps/core/hub/archive — ARC·1: the ONE definition of "this course is archived".
//
// A course record (crs_) may carry an OPTIONAL `archivedAt: <ISO>` — the no-migration accretion
// pattern (DATA-CONVENTIONS § 2, "hub records accrete under the same rule"). ABSENT = active, which
// is true of every course written before this lesson, so an existing droplet DB needs no data
// rewrite and no schema change: the change-log's payload column already holds the field. Archive
// sets it (patchRecord); unarchive DROPS the key (the `bookmarked` null-drop precedent) rather than
// writing a null, so an active course's payload stays byte-identical to what it was before.
//
// PURE, dependency-free, and bundled CLIENT-side (a subpath export like exams/exams, lib/pacing):
// the kids' apps read course records straight off the sync stream, so the same predicate that
// gates every server-side daily surface (the srs store, the countdown, the daily plan) decides the
// course list's active/archived split in the browser. One rule, not two.

/** Is this course record archived? Only a string `archivedAt` counts — a stray truthy is not a date. */
export function isArchived(course) {
  return !!course && typeof course.archivedAt === "string" && course.archivedAt.length > 0;
}

/** The ids of every archived course in `courses` (payloads). */
export function archivedCourseIds(courses) {
  const out = new Set();
  for (const c of Array.isArray(courses) ? courses : []) if (isArchived(c) && typeof c.id === "string") out.add(c.id);
  return out;
}

/** Courses that are NOT archived — what every active list and picker shows. */
export function activeCourses(courses) {
  return (Array.isArray(courses) ? courses : []).filter((c) => !isArchived(c));
}

/** Courses that ARE archived — the collapsed "Archived (N)" section. */
export function archivedCourses(courses) {
  return (Array.isArray(courses) ? courses : []).filter(isArchived);
}

/**
 * Records (assignments, exams, decks, meetings — anything carrying `courseId`) whose course is NOT
 * archived. A record with no `courseId` (an unlinked deck) is kept: it belongs to no course, so no
 * archive can hide it. The daily-surface exclusion seam for every course-linked collection.
 * @template T
 * @param {T[]} records
 * @param {object[]} courses  the course payloads (any collection listing works)
 * @returns {T[]}
 */
export function forActiveCourses(records, courses) {
  const archived = archivedCourseIds(courses);
  if (archived.size === 0) return Array.isArray(records) ? records : [];
  return (Array.isArray(records) ? records : []).filter((r) => !(r && typeof r.courseId === "string" && archived.has(r.courseId)));
}

/**
 * The due-shift an unarchive applies: how long the course sat archived, in whole milliseconds,
 * never negative (a clock that went backwards shifts nothing rather than pulling cards EARLIER).
 * @param {string} archivedAt  ISO
 * @param {string} now         ISO
 */
export function archivedSpanMs(archivedAt, now) {
  const a = Date.parse(archivedAt);
  const n = Date.parse(now);
  if (!Number.isFinite(a) || !Number.isFinite(n)) return 0;
  return Math.max(0, n - a);
}
